import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { 
  Package, 
  MapPin, 
  Truck, 
  CheckCircle2, 
  XCircle, 
  Building2, 
  Clock, 
  Zap, 
  DollarSign, 
  Rocket, 
  TrendingUp, 
  BarChart3,
  Mail,
  ChevronRight,
  Signal,
  Target,
  ArrowRight,
  Leaf,
  Battery,
  RotateCcw,
  Timer,
  GitBranch
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/cs17vxe9_0g%20Baltics_SSb-R00a_Mil.png";
const HERO_BG_URL = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/08492img_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_30_25%20PM.png";

// Header Component with scroll-based transparency
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-sm border-b border-slate-100' 
          : 'bg-transparent border-b border-transparent'
      }`} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a 
            href="https://0gbaltics.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center"
            data-testid="header-logo-link"
          >
            <img 
              src={LOGO_URL} 
              alt="0g Baltics" 
              className={`h-10 w-auto transition-all duration-300 ${!isScrolled ? 'brightness-0 invert' : ''}`}
              data-testid="header-logo"
            />
          </a>
          <Button 
            onClick={scrollToContact}
            className={`text-sm transition-all duration-300 ${
              isScrolled 
                ? 'btn-primary' 
                : 'bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30'
            }`}
            data-testid="header-cta"
          >
            Contact us
          </Button>
        </div>
      </div>
    </header>
  );
};

// Intersection Observer Hook for scroll animations
const useScrollAnimation = () => {
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, []);
  
  return ref;
};

// Sticker Visual Component
const StickerVisual = ({ large = false, label = "" }) => (
  <div className={`sticker-visual ${large ? 'sticker-visual-large' : ''}`} data-testid="sticker-visual">
    <span className="text-xs font-semibold text-slate-400 tracking-wider uppercase">{label || "IoT"}</span>
  </div>
);

// Section Components
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center pt-20 pb-12 md:py-32" 
      data-testid="hero-section"
      style={{
        backgroundImage: `url(${HERO_BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay filter */}
      <div className="absolute inset-0 bg-slate-900/70 md:bg-slate-900/60" />
      
      <div className="container-width relative z-10">
        <div className="max-w-3xl">
          <div className="fade-in-up">
            <h1 className="heading-h1 text-white mb-4 md:mb-6">
              Stick & Go – One-way tracking with a connected sticker
            </h1>
            <p className="text-lg md:text-2xl text-blue-300 font-semibold mb-3 md:mb-4">
              Stick it. Ship it. Know where it is.
            </p>
            <p className="text-base md:text-lg text-slate-200 mb-6 md:mb-8 max-w-xl leading-relaxed">
              A connected sticker designed for one-way logistics flows — no recovery, no reverse logistics, no operational burden.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                onClick={scrollToContact}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-md text-base w-full sm:w-auto"
                data-testid="hero-cta-pilot"
              >
                Start a pilot
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                onClick={scrollToContact}
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 font-semibold px-6 py-3 rounded-md text-base w-full sm:w-auto"
                data-testid="hero-cta-contact"
              >
                Contact us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = () => (
  <section id="problem" className="section-gray section-padding" data-testid="problem-section">
    <div className="container-width">
      <div className="max-w-3xl mx-auto text-center mb-12 fade-in-up">
        <h2 className="heading-h2 text-slate-900 mb-4">
          Tracking shouldn&apos;t create new logistics problems
        </h2>
      </div>
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {[
          { icon: MapPin, text: "Limited visibility during transport" },
          { icon: Truck, text: "Recovery and reverse logistics complexity" },
          { icon: DollarSign, text: "GPS cost and unrealistic expectations" },
          { icon: BarChart3, text: "Decisions based on assumptions instead of facts" }
        ].map((item, idx) => (
          <div key={idx} className={`fade-in-up stagger-${idx + 1} flex items-start gap-4 p-5 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default`}>
            <div className="icon-wrapper flex-shrink-0">
              <item.icon className="w-5 h-5" />
            </div>
            <p className="text-slate-700 font-medium">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const STICKER_ILLUSTRATION = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/2r5db8qp_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_25_24%20PM.png";

const SolutionSection = () => (
  <section id="solution" className="section-white section-padding" data-testid="solution-section">
    <div className="container-width">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
        <div className="fade-in-up order-2 lg:order-1">
          <h2 className="heading-h2 text-slate-900 mb-4 md:mb-6">
            Stick & Go — tracking that disappears
          </h2>
          <div className="body-text space-y-3 md:space-y-4">
            <p>
              <strong className="text-slate-900">One-way tracking service:</strong> The connected sticker becomes part of the shipment.
            </p>
            <p>No return. No retrieval. No device management.</p>
          </div>
        </div>
        <div className="fade-in-up stagger-2 flex justify-center order-1 lg:order-2">
          <img 
            src={STICKER_ILLUSTRATION} 
            alt="Connected sticker on package - tracking that disappears"
            className="rounded-xl shadow-lg w-full max-w-sm lg:max-w-full h-auto max-h-[250px] lg:max-h-[400px] object-contain"
            data-testid="solution-illustration"
          />
        </div>
      </div>
    </div>
  </section>
);

const ComparisonSection = () => (
  <section id="comparison" className="section-gray section-padding" data-testid="comparison-section">
    <div className="container-width">
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* What it is */}
        <div className="fade-in-up">
          <div className="comparison-positive rounded-xl p-8">
            <h3 className="heading-h3 text-slate-900 mb-6 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-blue-700" />
              What it is
            </h3>
            <ul className="check-list space-y-3">
              {[
                "Connected sticker, ultra-thin, self-contained",
                "Applied on goods, packaging or cellophane",
                "Automatically connected",
                "Fully operated service"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* What it is not */}
        <div className="fade-in-up stagger-2">
          <div className="comparison-negative rounded-xl p-8">
            <h3 className="heading-h3 text-slate-900 mb-6 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-slate-500" />
              What it is not
            </h3>
            <ul className="x-list space-y-3">
              {[
                "Not GPS",
                "Not recoverable",
                "Not returnable packaging",
                "Not a hardware project"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CONNECTED_STICKER_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/7zhror81_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_36_22%20PM.png";

const ConnectedStickerSection = () => (
  <section id="connected-sticker" className="section-white section-padding" data-testid="connected-sticker-section">
    <div className="container-width">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="fade-in-up order-2 lg:order-1">
          <h2 className="heading-h2 text-slate-900 mb-4 md:mb-6">
            Tracking becomes part of the shipment
          </h2>
          <ul className="space-y-3 md:space-y-4">
            {[
              { icon: Zap, text: "No activation" },
              { icon: Package, text: "No operator handling" },
              { icon: Target, text: "No recovery at destination" }
            ].map((item, idx) => (
              <li key={idx} className={`fade-in-up stagger-${idx + 1} flex items-center gap-3 md:gap-4`}>
                <div className="icon-wrapper">
                  <item.icon className="w-5 h-5" />
                </div>
                <span className="text-slate-700 font-medium text-base md:text-lg">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="fade-in-up stagger-3 flex justify-center order-1 lg:order-2">
          <img 
            src={CONNECTED_STICKER_IMG} 
            alt="Connected sticker tracking on pallets"
            className="rounded-xl shadow-lg w-full max-w-sm lg:max-w-full h-auto max-h-[250px] lg:max-h-[400px] object-contain"
            data-testid="connected-sticker-illustration"
          />
        </div>
      </div>
    </div>
  </section>
);

const VisibilitySection = () => (
  <section id="visibility" className="section-gray section-padding" data-testid="visibility-section">
    <div className="container-width">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 fade-in-up">
          <h2 className="heading-h2 text-slate-900 mb-4">
            We don&apos;t sell coordinates
          </h2>
          <p className="body-text max-w-2xl mx-auto">No map-based GPS tracking. We provide operational certainty.</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {[
            { 
              icon: Signal, 
              title: "Network-based location", 
              desc: "Reliable indication of where the shipment is, based on network coverage" 
            },
            { 
              icon: Building2, 
              title: "Site detection", 
              desc: "Detection of presence on defined sites: factories, hubs, warehouses" 
            },
            { 
              icon: Target, 
              title: "Clear events", 
              desc: "Operational events: left site, in transit, arrived on site" 
            }
          ].map((item, idx) => (
            <div 
              key={idx} 
              className={`fade-in-up stagger-${idx + 1} bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default`}
            >
              <div className="icon-wrapper mb-4">
                <item.icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
        
        <div className="fade-in-up max-w-2xl mx-auto">
          <div className="bg-blue-700 rounded-xl p-6 md:p-8 text-center">
            <p className="text-white font-semibold text-lg md:text-xl">
              &ldquo;Operational certainty, not cartographic precision.&rdquo;
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HOW_IT_WORKS_GIF = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/sautfks0_Untitled%20design.gif";
const STEP_APPLY_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/nbief47b_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_38_01%20PM.png";
const STEP_CONNECT_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/cdug8wc2_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_41_44%20PM.png";
const STEP_PROCESS_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/xjg9i9fr_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_44_08%20PM.png";
const STEP_DASHBOARD_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/kcew2kxd_ChatGPT%20Image%20Feb%203%2C%202026%2C%2002_46_40%20PM.png";

const HowItWorksSection = () => (
  <section id="how-it-works" className="section-white section-padding" data-testid="how-it-works-section">
    <div className="container-width">
      <div className="text-center mb-10 md:mb-16 fade-in-up">
        <h2 className="heading-h2 text-slate-900 mb-4">
          How it works
        </h2>
      </div>
      
      {/* Illustration GIF */}
      <div className="flex justify-center mb-12 fade-in-up">
        <img 
          src={HOW_IT_WORKS_GIF} 
          alt="How Stick & Go tracking works - animated illustration"
          className="rounded-xl shadow-lg w-full max-w-2xl h-auto"
          data-testid="how-it-works-illustration"
        />
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { num: "1", title: "Apply", desc: "Connected sticker applied to goods or packaging", img: STEP_APPLY_IMG },
          { num: "2", title: "Connect", desc: "Wide-area connectivity during transport", img: STEP_CONNECT_IMG },
          { num: "3", title: "Process", desc: "Intelligence layer: signal processing, site recognition, shipment states", img: STEP_PROCESS_IMG },
          { num: "4", title: "Dashboard", desc: "Operational dashboard with business states", img: STEP_DASHBOARD_IMG }
        ].map((step, idx) => (
          <div key={idx} className={`fade-in-up stagger-${idx + 1} bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 hover:border-blue-300 transition-all duration-300 cursor-default`}>
            {step.img ? (
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={step.img} 
                  alt={step.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center">
                <div className="step-number">{step.num}</div>
              </div>
            )}
            <div className="p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-blue-700 font-bold text-lg">{step.num}.</span>
                <h3 className="heading-h3 text-slate-900">{step.title}</h3>
              </div>
              <p className="text-slate-600 text-sm">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 md:mt-12 text-center fade-in-up">
        <p className="body-text italic">All intelligence and orchestration are fully operated.</p>
      </div>
    </div>
  </section>
);

const MULTISITE_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/gvkppafn_ChatGPT%20Image%20Feb%204%2C%202026%2C%2011_24_12%20AM.png";

const BenefitsSection = () => (
  <section id="benefits" className="section-gray section-padding" data-testid="benefits-section">
    <div className="container-width">
      {/* Multi-site tracking */}
      <div className="max-w-5xl mx-auto mb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center mb-10">
          {/* Image */}
          <div className="fade-in-up flex justify-center">
            <img 
              src={MULTISITE_IMG} 
              alt="Multi-site tracking - factories, hubs, warehouses connected"
              className="rounded-xl shadow-lg w-full max-w-md h-auto"
              data-testid="multisite-illustration"
            />
          </div>
          
          {/* Text + Cards */}
          <div className="fade-in-up">
            <h2 className="heading-h2 text-slate-900 mb-6">
              Multi-site tracking by design
            </h2>
            <div className="space-y-4">
              {[
                { icon: Building2, text: "Multiple defined sites (factories, hubs, warehouses, partners)" },
                { icon: Signal, text: "Detection of arrival, departure, transit" },
                { icon: Clock, text: "Timestamped site-to-site journey" }
              ].map((item, idx) => (
                <div key={idx} className={`flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300`}>
                  <div className="icon-wrapper flex-shrink-0">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <p className="text-slate-700 font-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits */}
      <div>
        <div className="text-center mb-10 fade-in-up">
          <h2 className="heading-h2 text-slate-900 mb-4">
            Benefits
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {[
            { icon: Zap, title: "Operational simplicity", desc: "No complexity, just visibility" },
            { icon: DollarSign, title: "Cost control", desc: "Predictable, scalable pricing" },
            { icon: Rocket, title: "Speed of deployment", desc: "Days, not months" },
            { icon: TrendingUp, title: "Scalability", desc: "From pilot to full rollout" },
            { icon: BarChart3, title: "Decision-ready data", desc: "Facts, not assumptions" }
          ].map((benefit, idx) => (
            <div key={idx} className={`fade-in-up stagger-${idx + 1} p-6 bg-white rounded-xl border border-slate-200 text-center hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default`}>
              <div className="icon-wrapper mx-auto mb-4">
                <benefit.icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-slate-600">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const SUSTAINABILITY_IMG = "https://customer-assets.emergentagent.com/job_track-n-ship-2/artifacts/uvy5fpuf_ChatGPT%20Image%20Feb%204%2C%202026%2C%2011_16_24%20AM.png";

const SustainabilitySection = () => (
  <section id="sustainability" className="section-white section-padding" data-testid="sustainability-section">
    <div className="container-width">
      <div className="max-w-5xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12">
          {/* Image */}
          <div className="fade-in-up order-1 lg:order-2 flex justify-center">
            <img 
              src={SUSTAINABILITY_IMG} 
              alt="Sustainable connected sticker - lithium-free, one-way tracking"
              className="rounded-xl shadow-lg w-full max-w-md h-auto"
              data-testid="sustainability-illustration"
            />
          </div>
          
          {/* Text content */}
          <div className="fade-in-up order-2 lg:order-1">
            <h2 className="heading-h2 text-slate-900 mb-2">
              Sustainability
            </h2>
            <h3 className="text-lg md:text-xl text-slate-600 font-medium mb-6">
              Designed for one-way logistics, not for waste
            </h3>
            <p className="body-text">
              Stick & Go was designed around a simple principle:
              <strong className="text-slate-900"> do not create more waste than necessary to deliver visibility.</strong>
            </p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {[
            { 
              icon: Battery, 
              title: "Lithium-free battery", 
              desc: "The connected sticker relies on a Zinc-Manganese battery, without lithium, reducing environmental and transport constraints." 
            },
            { 
              icon: RotateCcw, 
              title: "No reverse logistics", 
              desc: "One-way tracking means no recovery, no additional transport, and no unnecessary operational loops." 
            },
            { 
              icon: Timer, 
              title: "Right-sized lifetime", 
              desc: "The sticker lifetime is intentionally limited to the duration of a shipment, avoiding over-engineered multi-year batteries for short-lived use cases." 
            },
            { 
              icon: GitBranch, 
              title: "Open-loop by design", 
              desc: "Built specifically for open-loop supply chains where assets are not expected to return." 
            }
          ].map((item, idx) => (
            <div key={idx} className={`fade-in-up stagger-${idx + 1} p-6 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default`}>
              <div className="flex items-start gap-4">
                <div className="icon-wrapper flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="fade-in-up mt-10">
          <div className="bg-blue-700 rounded-xl p-6 md:p-8 text-center max-w-3xl mx-auto">
            <p className="text-white font-semibold text-lg md:text-xl">
              &ldquo;Sustainability here is not an add-on — it is a consequence of the usage model.&rdquo;
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const UseCasesSection = () => (
  <section id="use-cases" className="section-white section-padding" data-testid="use-cases-section">
    <div className="container-width">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12 fade-in-up">
          <h2 className="heading-h2 text-slate-900 mb-4">
            Typical use cases
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
    "Natural fibers & textiles",
            "One-way industrial packaging",
            "High-value consumable containers",
            "Circular supply chains with non-returnable legs",
            "Pilot shipments and temporary logistics flows"
          ].map((useCase, idx) => (
            <div key={idx} className={`fade-in-up stagger-${idx + 1} flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default`}>
              <ChevronRight className="w-5 h-5 text-blue-700 flex-shrink-0" />
              <span className="text-slate-700 font-medium">{useCase}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PilotSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <section id="pilot" className="section-gray section-padding" data-testid="pilot-section">
      <div className="container-width">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 fade-in-up">
            <h2 className="heading-h2 text-slate-900 mb-4">
              Pilot-first approach
            </h2>
          </div>
          <div className="feature-card fade-in-up">
            <ul className="space-y-3 mb-6">
              {[
                "Short paid pilot",
                "Limited scope",
                "Connected stickers included",
                "Service fully operated",
                "KPI-based evaluation"
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-700 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-200 pt-6 mt-6">
              <div className="grid sm:grid-cols-2 gap-4 text-center">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="font-semibold text-blue-700">GO → scale</p>
                </div>
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="font-semibold text-slate-600">NO-GO → stop, learnings delivered</p>
                </div>
              </div>
              <p className="text-center mt-6 text-slate-700 font-semibold">No lock-in.</p>
            </div>
          </div>
          <div className="text-center mt-8 fade-in-up stagger-2">
            <Button 
              onClick={scrollToContact}
              className="btn-primary text-base"
              data-testid="pilot-cta"
            >
              Start your pilot
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const USE_CASE_OPTIONS = [
  "Natural fibers & textiles",
  "One-way industrial packaging",
  "High-value consumable containers",
  "Circular supply chains (non-returnable legs)",
  "Pilot shipments / temporary logistics flows",
  "Other"
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
    use_cases: [],
    honeypot: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUseCaseChange = (useCase, checked) => {
    setFormData(prev => ({
      ...prev,
      use_cases: checked 
        ? [...prev.use_cases, useCase]
        : prev.use_cases.filter(uc => uc !== useCase)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus({ 
          type: 'success', 
          message: data.message || "Thank you. Your message has been sent. We'll get back to you shortly."
        });
        setFormData({ name: '', company: '', email: '', message: '', use_cases: [], honeypot: '' });
      } else {
        setStatus({ 
          type: 'error', 
          message: data.detail || 'Unable to send message. Please try again or contact us directly at info@0gbaltics.com'
        });
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Unable to send message. Please try again or contact us directly at info@0gbaltics.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-white section-padding" data-testid="contact-section">
      <div className="container-width">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8 fade-in-up">
            <h2 className="heading-h2 text-slate-900 mb-4">
              Contact us
            </h2>
            <p className="body-text">
              Tell us briefly about your use case.
            </p>
          </div>
          
          {/* Contact Form */}
          <div className="fade-in-up stagger-1">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Honeypot field - hidden from users */}
              <div className="absolute -left-[9999px]" aria-hidden="true">
                <label htmlFor="honeypot">Leave this field empty</label>
                <input
                  type="text"
                  id="honeypot"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Name Field */}
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-all"
                  placeholder="Your full name"
                  aria-label="Your full name"
                  data-testid="contact-name-input"
                />
              </div>

              {/* Company Field */}
              <div>
                <label 
                  htmlFor="company" 
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Company <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-all"
                  placeholder="Your company name"
                  aria-label="Your company name"
                  data-testid="contact-company-input"
                />
              </div>

              {/* Email Field */}
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={254}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-all"
                  placeholder="you@company.com"
                  aria-label="Your email address"
                  data-testid="contact-email-input"
                />
              </div>

              {/* Message Field */}
              <div>
                <label 
                  htmlFor="message" 
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={2000}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent transition-all resize-none"
                  placeholder="Tell us about your logistics use case..."
                  aria-label="Your message"
                  data-testid="contact-message-input"
                />
              </div>

              {/* Use Cases Checkboxes - Optional */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Use case(s) <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="use-cases-checkboxes">
                  {USE_CASE_OPTIONS.map((useCase) => (
                    <div key={useCase} className="flex items-center space-x-3">
                      <Checkbox
                        id={`usecase-${useCase}`}
                        checked={formData.use_cases.includes(useCase)}
                        onCheckedChange={(checked) => handleUseCaseChange(useCase, checked)}
                        className="border-slate-300 data-[state=checked]:bg-blue-700 data-[state=checked]:border-blue-700"
                        data-testid={`usecase-checkbox-${useCase.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      />
                      <label
                        htmlFor={`usecase-${useCase}`}
                        className="text-sm text-slate-700 cursor-pointer leading-tight"
                      >
                        {useCase}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Message */}
              {status.message && (
                <div 
                  className={`p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  role="alert"
                  data-testid="contact-status-message"
                >
                  <div className="flex items-center gap-2">
                    {status.type === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <p className="font-medium">{status.message}</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary text-base py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Send message
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
          </div>
          
          {/* Alternative Contact */}
          <div className="mt-8 text-center fade-in-up stagger-2">
            <div className="inline-flex items-center gap-2 p-4 bg-slate-50 rounded-lg">
              <Mail className="w-5 h-5 text-blue-700" />
              <span className="text-slate-600">Or email us directly:</span>
              <a 
                href="mailto:info@0gbaltics.com" 
                className="text-blue-700 font-semibold hover:underline"
                data-testid="alternative-email"
              >
                info@0gbaltics.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="footer-section py-12" data-testid="footer">
    <div className="container-width">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
        {/* Lithuania */}
        <div>
          <p className="footer-title">Lithuania</p>
          <p className="text-sm leading-relaxed">
            0g Baltics UAB<br />
            A. Goštauto g. 8-358<br />
            LT-01108 Vilnius
          </p>
        </div>
        
        {/* Latvia */}
        <div>
          <p className="footer-title">Latvia</p>
          <p className="text-sm leading-relaxed">
            0g Baltics SIA<br />
            Elizabetes iela 31-8<br />
            LV-1010 Riga
          </p>
        </div>
        
        {/* Estonia */}
        <div>
          <p className="footer-title">Estonia</p>
          <p className="text-sm leading-relaxed">
            E2 Technologies OÜ<br />
            Lõõtsa tn 5<br />
            11415 Tallinn
          </p>
        </div>
      </div>
      
      <div className="border-t border-slate-700 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
        <a 
          href="mailto:info@0gbaltics.com" 
          className="text-slate-400 hover:text-white transition-colors"
          data-testid="footer-email"
        >
          info@0gbaltics.com
        </a>
        <p className="text-sm text-slate-500">
          © 2026 0g Baltics. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  useScrollAnimation();
  
  return (
    <div className="App" data-testid="stick-and-go-landing">
      <Header />
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <ComparisonSection />
      <ConnectedStickerSection />
      <VisibilitySection />
      <HowItWorksSection />
      <BenefitsSection />
      <SustainabilitySection />
      <UseCasesSection />
      <PilotSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
